/**
 * Lightweight Real-User-Monitoring helper.
 *
 * Sentry's `browserTracingIntegration` already captures Core Web Vitals
 * under the hood. This helper layers a small server-side beacon on top.
 *
 * Wire into `main.tsx` once with:
 *
 *     import { initWebVitals } from './app/services/webVitals';
 *     initWebVitals();
 */

import { API_PATHS } from '../../lib/apiRoutes';
import { BACKEND_URL } from '../../lib/env';

interface WebVitalRecord {
  name: 'lcp' | 'cls' | 'fcp' | 'inp' | 'ttfb';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  navigationType?: string;
}

interface LargestContentfulPaintEntry extends PerformanceEntry {
  renderTime?: number;
  loadTime?: number;
}

interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput?: boolean;
  value?: number;
}

interface PerformanceEventTimingEntry extends PerformanceEntry {
  interactionId?: number;
  duration: number;
}

interface _Marker {
}

const RUM_BEACON_URL = new URL(API_PATHS.observability.rumVital, BACKEND_URL).toString();

const ratingFor = (name: WebVitalRecord['name'], value: number): WebVitalRecord['rating'] => {
  switch (name) {
    case 'lcp':
      return value <= 2_500 ? 'good' : value <= 4_000 ? 'needs-improvement' : 'poor';
    case 'inp':
      return value <= 200 ? 'good' : value <= 500 ? 'needs-improvement' : 'poor';
    case 'cls':
      return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs-improvement' : 'poor';
    case 'fcp':
      return value <= 1_800 ? 'good' : value <= 3_000 ? 'needs-improvement' : 'poor';
    case 'ttfb':
      return value <= 800 ? 'good' : value <= 1_800 ? 'needs-improvement' : 'poor';
    default:
      return 'good';
  }
};

const sentOnce = new Set<WebVitalRecord['name']>();
let clsAccum = 0;
let endFlushBound = false;
let latestLcp = 0;
let worstInp = 0;

const sendBeacon = (record: WebVitalRecord) => {
  try {
    const payload = JSON.stringify({ ...record, ts: Date.now(), path: window.location.pathname });
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(RUM_BEACON_URL, blob);
      return;
    }
    void fetch(RUM_BEACON_URL, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      credentials: 'omit'
    });
  } catch {
    // best-effort; never block the user
  }
};

/** One beacon per metric name per page load. */
const sendOnce = (record: WebVitalRecord) => {
  if (sentOnce.has(record.name)) return;
  sentOnce.add(record.name);
  sendBeacon(record);
};

const flushEndOfPageMetrics = () => {
  if (Number.isFinite(latestLcp) && latestLcp > 0) {
    sendOnce({ name: 'lcp', value: latestLcp, rating: ratingFor('lcp', latestLcp) });
  }
  if (Number.isFinite(worstInp) && worstInp > 0) {
    sendOnce({ name: 'inp', value: worstInp, rating: ratingFor('inp', worstInp) });
  }
  sendOnce({ name: 'cls', value: clsAccum, rating: ratingFor('cls', clsAccum) });
};

const bindEndFlush = () => {
  if (endFlushBound) return;
  endFlushBound = true;
  window.addEventListener('pagehide', flushEndOfPageMetrics, { once: true });
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEndOfPageMetrics();
  });
  window.setTimeout(flushEndOfPageMetrics, 8_000);
};

const observe = (entryType: string, handler: (entry: PerformanceEntry) => void) => {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const obs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) handler(entry);
    });
    obs.observe({ type: entryType, buffered: true });
  } catch {
    // entry type unsupported on this browser
  }
};

let initialized = false;

export const initWebVitals = (): void => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  bindEndFlush();

  observe('largest-contentful-paint', (entry) => {
    const lcp = entry as LargestContentfulPaintEntry;
    const value = lcp.renderTime || lcp.loadTime || entry.startTime;


    if (Number.isFinite(value)) latestLcp = Number(value);
  });

  observe('paint', (entry) => {
    if (entry.name === 'first-contentful-paint') {
      const value = entry.startTime;
      sendOnce({ name: 'fcp', value, rating: ratingFor('fcp', value) });
    }
  });

  observe('layout-shift', (entry) => {
    const e = entry as LayoutShiftEntry;
    if (!e.hadRecentInput && Number.isFinite(e.value)) {
      clsAccum += Number(e.value || 0);
    }
  });

  observe('event', (entry) => {
    const e = entry as PerformanceEventTimingEntry;
    if (e.interactionId && Number.isFinite(e.duration) && e.duration > worstInp) {
      worstInp = e.duration;
    }
  });

  observe('navigation', (entry) => {
    const nav = entry as PerformanceNavigationTiming;
    const ttfb = Math.max(0, nav.responseStart - nav.requestStart);
    sendOnce({ name: 'ttfb', value: ttfb, rating: ratingFor('ttfb', ttfb), navigationType: nav.type });
  });
};
